import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationContainer } from 'entities/Authentication';
import { Button, Flex, Typography } from 'shared/_ui';
import { getTelegramBotUrl } from 'shared/consts/telegram';
import {
  downloadSetupConfig,
  loadSetupInstructions,
  loadSetupValues,
  resolveSetup,
  SetupInstructions,
  SetupInstructionsError,
  SetupLinkError,
  SetupView,
} from '../api/setupApi';
import { detectPlatform, SetupPlatformId } from '../model/platforms';
import styles from './styles.module.scss';
import { InstructionBlocks } from './InstructionBlocks';
import { needsValues, SetupValues } from '../model/instructions';

type PageState =
  | 'loading'
  | 'ready'
  | 'expired'
  | 'error'
  | 'instructionsError';

const readToken = () =>
  new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';

export const SetupPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.split('-')[0] === 'ru' ? 'ru' : 'en';
  const [token] = useState(readToken);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [setup, setSetup] = useState<SetupView | null>(null);
  const [instructions, setInstructions] = useState<SetupInstructions | null>(
    null
  );
  const [selectedPlatform, setSelectedPlatform] = useState<SetupPlatformId>(
    () => detectPlatform(window.navigator.userAgent)
  );
  const [downloading, setDownloading] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [values, setValues] = useState<SetupValues | null>(null);
  const [valuesError, setValuesError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [valuesRetry, setValuesRetry] = useState(0);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        document.title,
        `${window.location.pathname}${window.location.search}`
      );
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setPageState('expired');
      return;
    }
    let cancelled = false;
    setPageState('loading');
    setInstructions(null);
    setSelectedClient('');
    resolveSetup(token)
      .then((view) => {
        if (cancelled) return undefined;
        setSetup(view);
        return loadSetupInstructions(locale);
      })
      .then((catalog) => {
        if (cancelled || !catalog) return;
        setInstructions(catalog);
        setSelectedPlatform((current) =>
          catalog.platforms.some(({ platformId }) => platformId === current)
            ? current
            : catalog.platforms[0].platformId
        );
        setPageState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof SetupLinkError) {
          setPageState(error.expired ? 'expired' : 'error');
          return;
        }
        setPageState(
          error instanceof SetupInstructionsError
            ? 'instructionsError'
            : 'error'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [locale, token, retry]);

  const platform =
    instructions?.platforms.find(
      ({ platformId }) => platformId === selectedPlatform
    ) ?? instructions?.platforms[0];

  const client =
    platform?.clients.find((item) => item.id === selectedClient) ??
    platform?.clients.find((item) => item.recommended) ??
    platform?.clients[0];
  const requiresValues = Boolean(
    client && needsValues([...client.blocks, ...client.troubleshooting])
  );
  useEffect(() => {
    if (!requiresValues || values !== null || pageState !== 'ready') return;
    let cancelled = false;
    setValuesError(false);
    loadSetupValues(token)
      .then((result) => {
        if (!cancelled) setValues(result);
      })
      .catch(() => {
        if (!cancelled) setValuesError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [requiresValues, values, token, pageState, valuesRetry]);

  const downloadConfig = async (kind: string) => {
    if (!setup?.configKinds.includes(kind)) return;
    setDownloading(true);
    try {
      await downloadSetupConfig(token, kind);
    } catch (error) {
      setPageState(
        error instanceof SetupLinkError && error.expired ? 'expired' : 'error'
      );
    } finally {
      setDownloading(false);
    }
  };

  if (
    pageState === 'loading' ||
    (pageState === 'ready' && instructions?.locale !== locale)
  ) {
    return (
      <AuthenticationContainer title="Private Gate">
        <Typography.Text>{t('setup.loading')}</Typography.Text>
      </AuthenticationContainer>
    );
  }

  if (pageState === 'expired') {
    return (
      <AuthenticationContainer title={t('setup.expired.title')}>
        <Flex direction="column" spacing={20}>
          <Typography.Text type="secondary">
            {t('setup.expired.description')}
          </Typography.Text>
          <a
            className={styles.recoveryAction}
            href={getTelegramBotUrl()}
            target="_blank"
            rel="noreferrer"
          >
            {t('setup.expired.botAction')}
          </a>
        </Flex>
      </AuthenticationContainer>
    );
  }

  if (pageState === 'instructionsError') {
    return (
      <AuthenticationContainer title={t('setup.instructionsError.title')}>
        <Flex direction="column" spacing={20}>
          <Typography.Text type="secondary">
            {t('setup.instructionsError.description')}
          </Typography.Text>
          <a className={styles.recoveryAction} href="mailto:support@irs-lab.fr">
            {t('setup.supportAction')}
          </a>
          <Button
            type="secondary"
            onClick={() => setRetry((current) => current + 1)}
          >
            {t('setup.retryAction')}
          </Button>
        </Flex>
      </AuthenticationContainer>
    );
  }

  if (pageState === 'error' || !platform || !client) {
    return (
      <AuthenticationContainer title={t('setup.error.title')}>
        <Flex direction="column" spacing={20}>
          <Typography.Text type="secondary">
            {t('setup.error.description')}
          </Typography.Text>
          <Button
            type="secondary"
            onClick={() => setRetry((current) => current + 1)}
          >
            {t('setup.retryAction')}
          </Button>
        </Flex>
      </AuthenticationContainer>
    );
  }

  return (
    <AuthenticationContainer title={t('setup.ready.title')}>
      <Flex direction="column" spacing={24} className={styles.wrapper}>
        <Typography.Text type="secondary">
          {t('setup.ready.description')}
        </Typography.Text>
        <label className={styles.block}>
          {t('setup.expanded.language')}
          <select
            aria-label={t('setup.expanded.language')}
            value={locale}
            onChange={(event) => {
              setSelectedClient('');
              void i18n.changeLanguage(event.target.value);
            }}
          >
            <option value="ru">Русский</option>
            <option value="en">English</option>
          </select>
        </label>

        <div
          className={styles.selector}
          role="tablist"
          aria-label={t('setup.platformSelector')}
        >
          {instructions?.platforms.map((item) => (
            <button
              key={item.platformId}
              type="button"
              role="tab"
              aria-selected={item.platformId === selectedPlatform}
              className={
                item.platformId === selectedPlatform
                  ? styles.activeTab
                  : styles.tab
              }
              onClick={() => {
                setSelectedPlatform(item.platformId);
                setSelectedClient('');
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <section className={styles.card}>
          <Typography.Title level={3}>{platform.label}</Typography.Title>
          <label className={styles.block}>
            {t('setup.expanded.client')}
            <select
              aria-label={t('setup.expanded.client')}
              value={client.id}
              onChange={(event) => setSelectedClient(event.target.value)}
            >
              {platform.clients.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.recommended
                    ? t('setup.recommendedApp', { appName: item.app })
                    : item.app}
                </option>
              ))}
            </select>
          </label>
          {client.badge && (
            <Typography.Text type="secondary">{client.badge}</Typography.Text>
          )}
          {valuesError && requiresValues && (
            <div className={styles.notice}>
              <p>{t('setup.expanded.valuesError')}</p>
              <a href="mailto:support@irs-lab.fr">support@irs-lab.fr</a>
              <Button
                type="secondary"
                onClick={() => setValuesRetry((current) => current + 1)}
              >
                {t('setup.retryAction')}
              </Button>
            </div>
          )}
          <div
            className={styles.block}
            key={`${locale}-${platform.platformId}-${client.id}`}
          >
            {!client.blocks.some((block) => block.type === 'sources') && (
              <InstructionBlocks
                blocks={[{ type: 'sources', items: [], blocks: [] }]}
                client={client}
                values={values ?? {}}
                configKinds={setup?.configKinds ?? []}
                download={downloadConfig}
                downloading={downloading}
              />
            )}
            <InstructionBlocks
              blocks={[...client.blocks, ...client.troubleshooting]}
              client={client}
              values={values ?? {}}
              configKinds={setup?.configKinds ?? []}
              download={downloadConfig}
              downloading={downloading}
            />
          </div>
        </section>
      </Flex>
    </AuthenticationContainer>
  );
};
